import { graphql } from 'react-relay';
import { useQuery } from '@atlassian/jira-relay-apollo-migration-utils/src/services/use-query/index.tsx';
import type {
	perIssueMetricsRelayQuery$variables,
	perIssueMetricsRelayQuery$data,
} from '@atlassian/jira-relay/src/__generated__/perIssueMetricsRelayQuery.graphql';
import type { ProjectId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { ServiceQueryResultWithFetch } from '@atlassian/jira-software-deployments-service-query-types/src/services/types.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { NUM_ISSUES_PER_PAGE } from '../../common/constants';
import type { DateRange, IssueMetrics, TopFilter } from '../../common/types';
import { buildQueryVariables, transformResult } from './utils';

export const usePerIssueMetrics = (
	projectId: ProjectId | null,
	selectedDateRange: DateRange | null,
	filters: TopFilter,
): ServiceQueryResultWithFetch<IssueMetrics[]> => {
	const cloudId = useCloudId();

	const variables = buildQueryVariables(
		cloudId,
		projectId,
		selectedDateRange,
		NUM_ISSUES_PER_PAGE,
		filters,
	);
	const skip = projectId === null || selectedDateRange === null;

	const queryResult = useQuery<perIssueMetricsRelayQuery$variables, perIssueMetricsRelayQuery$data>(
		// disabling eslint rule since transformation is happaning inside transformQueryResult()
		/* eslint-disable @atlassian/relay/unused-fields */
		graphql`
			query perIssueMetricsRelayQuery($filter: DevOpsMetricsPerIssueMetricsFilter!, $first: Int!) {
				devOpsMetrics {
					perIssueMetrics(filter: $filter, first: $first) {
						nodes {
							id
							totalCycleTimeSeconds
							meanReviewTimeSeconds
							commitsCount
							pullRequestsCount
							lastSuccessfulProductionDeployment
						}
					}
				}
			}
		`,
		/* eslint-enable @atlassian/relay/unused-fields */
		{
			variables: { ...variables },
			// When projectId is not ready, query won't be executed
			skip,
		},
	);

	return skip ? { loading: true, fetch: () => Promise.resolve() } : transformResult(queryResult);
};
